import React, { useState, useEffect, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Loader2, Plus, Trash2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import api from '@/services/api';
import { Currency } from '@/hooks/usePriceMetadata';
import { useSettingspageTranslations } from '@/hooks/useSettingspageTranslations';
import { logDebug } from '@/utils/errorHandler';

interface ManageCurrenciesModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: (currency: Currency) => void;
}

export function ManageCurrenciesModal({ open, onClose, onSuccess }: ManageCurrenciesModalProps) {
  const [isCreating, setIsCreating] = useState(false);
  const queryClient = useQueryClient();
  const i18n = useSettingspageTranslations();

  const currencySchema = z.object({
    iso_code: z
      .string()
      .min(1, 'ISO code is required')
      .max(3, 'ISO code must be exactly 3 characters')
      .regex(/^[A-Z]+$/, 'ISO code must contain only uppercase letters'),
    name: z.string().min(1, 'Currency name is required'),
    symbol: z.string().min(1, 'Currency symbol is required'),
    decimals: z.number().min(0).max(4).default(2),
  });

  type CurrencyFormValues = z.infer<typeof currencySchema>;

  const form = useForm<CurrencyFormValues>({
    resolver: zodResolver(currencySchema),
    defaultValues: {
      iso_code: '',
      name: '',
      symbol: '',
      decimals: 2,
    },
  });

  // Reset state when modal closes - using useCallback to prevent unnecessary re-renders
  const resetModalState = useCallback(() => {
    setIsCreating(false);
    form.reset();
  }, [form]);

  useEffect(() => {
    if (!open) {
      resetModalState();
    }
  }, [open, resetModalState]);

  // Fetch organization currencies
  const {
    data: currencies = [],
    isLoading,
    refetch,
    error,
  } = useQuery<Currency[]>({
    queryKey: ['currencies'],
    queryFn: async () => {
      const response = await api.get('/api/currencies/');
      return response.data;
    },
    enabled: open,
    retry: 3,
    retryDelay: 1000,
  });

  // Create currency mutation
  const { mutate: createCurrency, isPending: isCreatingCurrency } = useMutation({
    mutationFn: async (data: CurrencyFormValues) => {
      try {
        const response = await api.post('/api/currencies/', data);
        return response.data;
      } catch (error: any) {
        // Log the full error details for debugging
        logDebug('Currency creation error', {
          status: error.response?.status,
          data: error.response?.data,
          headers: error.response?.headers,
          requestData: data,
          error,
        });
        throw error;
      }
    },
    onSuccess: newCurrency => {
      toast.success(i18n.organization.manageCurrencies.createSuccess);
      form.reset();
      setIsCreating(false);
      refetch();
      // Invalidate all related queries
      queryClient.invalidateQueries({ queryKey: ['currencies'] });
      queryClient.invalidateQueries({ queryKey: ['organization'] });
      queryClient.invalidateQueries({ queryKey: ['locales'] });
      queryClient.invalidateQueries({ queryKey: ['channels'] });
      onSuccess?.(newCurrency);
    },
    onError: (error: any) => {
      const errorDetails = error?.response?.data;
      let message = i18n.organization.manageCurrencies.createError;

      if (errorDetails) {
        // Handle specific validation errors
        if (errorDetails.iso_code) {
          message = errorDetails.iso_code[0];
        } else if (errorDetails.detail) {
          message = errorDetails.detail;
        } else if (errorDetails.non_field_errors) {
          message = errorDetails.non_field_errors[0];
        }
      }

      toast.error(message);
    },
  });

  // Delete (detach) currency mutation
  const { mutate: deleteCurrency } = useMutation({
    mutationFn: async (currencyId: string) => {
      // Soft delete - detach from organization
      await api.delete(`/api/currencies/${currencyId}/`);
    },
    onSuccess: () => {
      toast.success(i18n.organization.manageCurrencies.deleteSuccess);
      refetch();
      // Invalidate all related queries
      queryClient.invalidateQueries({ queryKey: ['currencies'] });
      queryClient.invalidateQueries({ queryKey: ['organization'] });
      queryClient.invalidateQueries({ queryKey: ['locales'] });
      queryClient.invalidateQueries({ queryKey: ['channels'] });
      // Trigger parent component refetch
      onSuccess?.(null as any); // This will trigger useOrgSettings refetch
    },
    onError: (error: any) => {
      const message =
        error?.response?.data?.detail || i18n.organization.manageCurrencies.deleteError;
      toast.error(message);
    },
  });

  const handleSubmit = (data: CurrencyFormValues) => {
    createCurrency(data);
  };

  const handleDelete = (currency: Currency) => {
    const confirmMessage = i18n.organization.manageCurrencies.deleteConfirm
      .replace('{{name}}', currency.name)
      .replace('{{code}}', currency.iso_code);

    if (confirm(confirmMessage)) {
      deleteCurrency(currency.iso_code);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose} key="manage-currencies-modal">
      <DialogContent className="max-h-[80vh] max-w-4xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{i18n.organization.manageCurrencies.title}</DialogTitle>
          <DialogDescription>{i18n.organization.manageCurrencies.description}</DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Create Currency Section */}
          <div className="rounded-lg border p-4">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-lg font-medium">
                {i18n.organization.manageCurrencies.addNewTitle}
              </h3>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setIsCreating(!isCreating)}
              >
                <Plus className="mr-2 size-4" />
                {isCreating
                  ? i18n.organization.manageCurrencies.cancelButton
                  : i18n.organization.manageCurrencies.addCurrencyButton}
              </Button>
            </div>

            {isCreating && (
              <Form {...form}>
                <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="iso_code"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{i18n.organization.manageCurrencies.isoCodeLabel}</FormLabel>
                          <FormControl>
                            <Input
                              {...field}
                              placeholder={i18n.organization.manageCurrencies.isoCodePlaceholder}
                              maxLength={3}
                              style={{ textTransform: 'uppercase' }}
                              onChange={e => field.onChange(e.target.value.toUpperCase())}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="symbol"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{i18n.organization.manageCurrencies.symbolLabel}</FormLabel>
                          <FormControl>
                            <Input
                              {...field}
                              placeholder={i18n.organization.manageCurrencies.symbolPlaceholder}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="name"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{i18n.organization.manageCurrencies.nameLabel}</FormLabel>
                          <FormControl>
                            <Input
                              {...field}
                              placeholder={i18n.organization.manageCurrencies.namePlaceholder}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="decimals"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{i18n.organization.manageCurrencies.decimalsLabel}</FormLabel>
                          <FormControl>
                            <Input
                              {...field}
                              type="number"
                              min={0}
                              max={4}
                              value={field.value}
                              onChange={e => field.onChange(parseInt(e.target.value) || 0)}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button type="submit" disabled={isCreatingCurrency}>
                      {isCreatingCurrency && <Loader2 className="mr-2 size-4 animate-spin" />}
                      {i18n.organization.manageCurrencies.createButton}
                    </Button>
                    <Button type="button" variant="outline" onClick={() => setIsCreating(false)}>
                      {i18n.organization.manageCurrencies.cancelButton}
                    </Button>
                  </div>
                </form>
              </Form>
            )}
          </div>

          {/* Currencies List */}
          <div className="rounded-lg border">
            <div className="border-b p-4">
              <h3 className="text-lg font-medium">
                {i18n.organization.manageCurrencies.organizationCurrenciesTitle}
              </h3>
              <p className="text-sm text-muted-foreground">
                {i18n.organization.manageCurrencies.organizationCurrenciesDescription}
              </p>
            </div>

            {isLoading ? (
              <div className="p-8 text-center">
                <Loader2 className="mx-auto size-8 animate-spin" />
                <p className="mt-2 text-sm text-muted-foreground">
                  {i18n.organization.manageCurrencies.loadingMessage}
                </p>
              </div>
            ) : error ? (
              <div className="p-8 text-center">
                <p className="text-sm text-red-600">Failed to load currencies. Please try again.</p>
              </div>
            ) : !currencies || currencies.length === 0 ? (
              <div className="p-8 text-center">
                <p className="text-sm text-muted-foreground">
                  {i18n.organization.manageCurrencies.noCurrenciesMessage}
                </p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{i18n.organization.manageCurrencies.isoCodeLabel}</TableHead>
                    <TableHead>{i18n.organization.manageCurrencies.nameLabel}</TableHead>
                    <TableHead>{i18n.organization.manageCurrencies.symbolLabel}</TableHead>
                    <TableHead>{i18n.organization.manageCurrencies.decimalsLabel}</TableHead>
                    <TableHead className="text-right">
                      {i18n.organization.manageLocales.actionsHeader}
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {currencies.map((currency, index) => {
                    // Generate a unique key to prevent React reconciliation issues
                    const uniqueKey = `currency-${currency.iso_code || `idx-${index}`}-${currency.name || 'unknown'}`;
                    return (
                      <TableRow key={uniqueKey}>
                        <TableCell>
                          <Badge variant="outline">{currency.iso_code || 'N/A'}</Badge>
                        </TableCell>
                        <TableCell>{currency.name || 'Unknown'}</TableCell>
                        <TableCell>
                          <Badge variant="secondary">{currency.symbol || '?'}</Badge>
                        </TableCell>
                        <TableCell>
                          {currency.decimals !== undefined ? currency.decimals : 2}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(currency)}
                            className="text-destructive hover:text-destructive"
                          >
                            <Trash2 className="size-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            {i18n.organization.manageCurrencies.closeButton}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
